import type {
	AlertsInfoResponseModel,
	PricingCampaignModel,
} from '@/shared/generated'

import { useNavigate } from '@tanstack/react-router'
import {
	any,
	filter,
	groupBy,
	map,
	mapValues,
	pipe,
	prop,
	sum,
} from 'lodash/fp'
import { CheckCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSelectedCurrency } from '@/entities/currency'
import { useOptimizationGroupQuery } from '@/entities/optimization-group'
import {
	getPricingAlertsTree,
	PricingAlerts,
	useMultiplePricingAlertsQuery,
	usePricingAlertsAnnotationsQuery,
	usePricingAlertsQuery,
} from '@/entities/price-alert'
import { usePricingCampaignsQuery } from '@/entities/pricing-campaign'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import FatalError from '@/shared/components/FatalError'
import { Operators } from '@/shared/components/query-builder'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/shared/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { AlertGroupBy } from '@/shared/generated'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryString } from '@/shared/tools/location'

type AlertsDrawerCommonProps = {
	isOpen: boolean
	close(): void
	drawerClassName?: string
}
type AlertsDrawerByPricingCampaignProps = {
	pricingCampaign: Pick<PricingCampaignModel, 'id' | 'name'>
	optimizationGroupIds?: string[]
	hasSolution?: boolean
}
type AlertsDrawerByOptimizationsProps = {
	scenarioId: string
	optimization: {
		optimization_id: string
		optimization_group_id: string
	}
	name: string
}
type AlertsDrawerByOptimizationGroupProps = {
	pricingCampaign?: Pick<PricingCampaignModel, 'id' | 'name'>
	optimizationGroupIds: string[]
	hasSolution?: boolean
}
export type AlertsDrawerProps = AlertsDrawerCommonProps & {
	type:
		'byPricingCampaign' | 'byOptimizationGroups' | 'global' | 'byOptimizations'
	pricingCampaign?: Pick<PricingCampaignModel, 'id' | 'name'>
	optimizationGroupIds?: string[]
	hasSolution?: boolean
	optimization?: {
		optimization_id: string
		optimization_group_id: string
	}
	name?: string
	scenarioId?: string
}

export function PricingAlertsDrawer({
	isOpen,
	close,
	type,
	...props
}: AlertsDrawerProps) {
	return (
		<Sheet
			modal={false}
			defaultOpen={isOpen}
			open={isOpen}
			onOpenChange={(open) => !open && close()}
		>
			<SheetContent
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
				disbleOverlay
				className='inset-y-2 right-2 h-auto w-[600px] gap-0 rounded-3xl border bg-background/80 backdrop-blur-xl sm:max-w-[600px]'
			>
				<ErrorBoundary>
					{type === 'byPricingCampaign' && (
						<PricingAlertsByPricingCampaign
							{...(props as AlertsDrawerByPricingCampaignProps)}
							close={close}
						/>
					)}
					{type === 'byOptimizationGroups' && (
						<PricingAlertsByOptimizationGroups
							{...(props as AlertsDrawerByOptimizationGroupProps)}
						/>
					)}
					{type === 'global' && <GlobalPricingAlerts close={close} />}
					{type === 'byOptimizations' && (
						<PricingAlertsByOptimizations
							close={close}
							{...(props as AlertsDrawerByOptimizationsProps)}
						/>
					)}
				</ErrorBoundary>
			</SheetContent>
		</Sheet>
	)
}
export function PricingAlertsByOptimizations({
	optimization,
	name,
	scenarioId,
}: AlertsDrawerByOptimizationsProps & { close(): void }) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const { optimization_group_id } = optimization
	const pricingAlertsAnnotationsQuery = usePricingAlertsAnnotationsQuery()
	const pricingAlertsByOptimizationQuery = useMultiplePricingAlertsQuery({
		group_by: AlertGroupBy.OptimizationGroupId,
		optimizations: [optimization],
		currency,
	})
	const pricingCampaignsQuery = usePricingCampaignsQuery({
		optimization_group_id,
		scenario_id: scenarioId,
	})
	const pricingAlertsByPricingCampaign = useMultiplePricingAlertsQuery<
		Record<string, string[]>
	>(
		{
			group_by: AlertGroupBy.PricingCampaignId,
			optimizations: [optimization],
			currency,
		},
		{
			select: pipe(
				prop('by_id'),
				groupBy('id'),
				mapValues(map('pricing_campaign_id'))
			),
		}
	)
	const isLoading = any(prop('isLoading'), [
		pricingAlertsByOptimizationQuery,
		pricingAlertsAnnotationsQuery,
		pricingCampaignsQuery,
		pricingAlertsByPricingCampaign,
	])
	const isError = any(prop('isError'), [
		pricingAlertsByOptimizationQuery,
		pricingAlertsAnnotationsQuery,
		pricingCampaignsQuery,
		pricingAlertsByPricingCampaign,
	])
	if (isLoading) {
		return <AlertsListSkeleton />
	}
	if (isError) {
		return <FatalError />
	}
	const totalAlerts = sum(
		map(prop('products'), pricingAlertsByOptimizationQuery.data?.by_level) || 0
	)
	const totalAlertsFormatted = formatNumber(totalAlerts)
	const alertsTree = getPricingAlertsTree({
		pricingAlerts: pricingAlertsByOptimizationQuery.data,
		annotations: pricingAlertsAnnotationsQuery.data,
		solutionData: {
			pricingCampaigns: pricingCampaignsQuery.data!,
			alertsByPc: pricingAlertsByPricingCampaign.data!,
			optimizationGroupId: optimization_group_id,
		},
	})

	return (
		<Layout className='h-full overflow-y-hidden'>
			<SheetHeader>
				<SheetTitle className='text-base'>{name}</SheetTitle>
				<SheetDescription>
					{t('pricing_alerts.drawer.optimization.subtitle', {
						total: totalAlertsFormatted,
						defaultValue: `${totalAlertsFormatted} alerts total for selected scenario, with the number of SKUs and Revenue share (%) highlighted for each alert type`,
					})}
				</SheetDescription>
			</SheetHeader>
			<Content className='overflow-y-auto'>
				<div className='w-full px-4'>
					{!alertsTree?.length ? (
						<Empty className='flex h-full flex-col items-center justify-center'>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<CheckCircleIcon height={50} width={50} />
								</EmptyMedia>
								<EmptyTitle>
									{t('general_everything_is_ok', {
										defaultValue: 'Everything is ok',
									})}
								</EmptyTitle>
								<EmptyDescription>
									{t('general_everything_is_ok_description', {
										defaultValue:
											'Everything is ok. You have no pricing alerts.',
									})}
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<PricingAlerts
							className='pb-20'
							collapsedByDefault
							alertsOf='whatif'
							alertsTree={alertsTree}
						/>
					)}
				</div>
			</Content>
		</Layout>
	)
}

export function PricingAlertsByOptimizationGroups({
	optimizationGroupIds,
}: {
	optimizationGroupIds: string[]
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const navigate = useNavigate()
	const optimizationGroupId = optimizationGroupIds[0]
	const { data: optimizationGroup } =
		useOptimizationGroupQuery(optimizationGroupId)
	const pricingAlertsAnnotationsQuery = usePricingAlertsAnnotationsQuery()
	const pricingAlertsByOptimizationGroupId =
		usePricingAlertsQuery<AlertsInfoResponseModel>(
			{
				group_by: AlertGroupBy.OptimizationGroupId,
				total_by_group: true,
				optimization_group_ids: [optimizationGroupId],
				currency,
			},
			{
				select: (data) => ({
					...data,
					by_level: filter(
						{ optimization_group_id: optimizationGroupId },
						data.by_level
					),
					by_id: filter(
						{ optimization_group_id: optimizationGroupId },
						data.by_id
					),
				}),
			}
		)
	const pricingAlertsByPricingCampaign = usePricingAlertsQuery<
		Record<string, string[]>
	>(
		{
			group_by: AlertGroupBy.PricingCampaignId,
			optimization_group_ids: [optimizationGroupId],
			currency,
		},
		{
			select: pipe(
				prop('by_id'),
				groupBy('id'),
				mapValues(map('pricing_campaign_id'))
			),
		}
	)
	const pricingCampaignsQuery = usePricingCampaignsQuery({
		optimization_group_id: optimizationGroupId,
	})
	const isLoading = any(prop('isLoading'), [
		pricingAlertsByOptimizationGroupId,
		pricingAlertsAnnotationsQuery,
		pricingCampaignsQuery,
		pricingAlertsByPricingCampaign,
	])
	const isError = any(prop('isError'), [
		pricingAlertsByOptimizationGroupId,
		pricingAlertsAnnotationsQuery,
		pricingCampaignsQuery,
		pricingAlertsByPricingCampaign,
	])
	if (isLoading) {
		return <AlertsListSkeleton />
	}
	if (isError) {
		return <FatalError />
	}
	const alertsTree = getPricingAlertsTree({
		pricingAlerts: pricingAlertsByOptimizationGroupId.data,
		annotations: pricingAlertsAnnotationsQuery.data,
		solutionData: {
			pricingCampaigns: pricingCampaignsQuery.data!,
			alertsByPc: pricingAlertsByPricingCampaign.data!,
			optimizationGroupId,
		},
	})
	const handleNavigate = (ids: string[]) =>
		navigate({
			to: `/p/og/${optimizationGroupId}/products${getFiltersQueryString([
				{
					name: 'alerts',
					operation: Operators.ANY_OF,
					value: ids,
				},
			])}`,
		})
	const totalAlertsFormatted = formatNumber(
		sum(
			pricingAlertsByOptimizationGroupId?.data?.by_level?.map(prop('products'))
		) || 0
	)

	return (
		<Layout className='h-full overflow-y-hidden'>
			<SheetHeader>
				<SheetTitle className='text-base'>{optimizationGroup?.name}</SheetTitle>
				<SheetDescription>
					{t('pricing_alerts.drawer.optimization_group.subtitle', {
						total: totalAlertsFormatted,
						defaultValue: `${totalAlertsFormatted} alerts total for selected OG`,
					})}
				</SheetDescription>
			</SheetHeader>
			<Content className='overflow-y-auto'>
				<div className='w-full px-4'>
					{!alertsTree?.length ? (
						<Empty className='flex h-full flex-col items-center justify-center'>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<CheckCircleIcon height={50} width={50} />
								</EmptyMedia>
								<EmptyTitle>
									{t('general_everything_is_ok', {
										defaultValue: 'Everything is ok',
									})}
								</EmptyTitle>
								<EmptyDescription>
									{t('general_everything_is_ok_description', {
										defaultValue:
											'Everything is ok. You have no pricing alerts.',
									})}
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<PricingAlerts
							className='pb-20'
							collapsedByDefault
							alertsOf='og'
							alertsTree={alertsTree}
							onNavigate={handleNavigate}
						/>
					)}
				</div>
			</Content>
		</Layout>
	)
}
export function PricingAlertsByPricingCampaign({
	pricingCampaign,
	optimizationGroupIds,
	close,
}: {
	pricingCampaign: Pick<PricingCampaignModel, 'id' | 'name'>
	optimizationGroupIds?: string[]
	close(): void
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const navigate = useNavigate()
	const pricingAlertsAnnotations = usePricingAlertsAnnotationsQuery()
	const pricingAlerts = usePricingAlertsQuery(
		{
			group_by: AlertGroupBy.PricingCampaignId,
			optimization_group_ids: optimizationGroupIds,
			currency,
		},
		{
			select: (data) => ({
				...data,
				by_level: filter(
					{ pricing_campaign_id: pricingCampaign.id },
					data.by_level
				),
				by_id: filter({ pricing_campaign_id: pricingCampaign.id }, data.by_id),
			}),
		}
	)
	const isLoading =
		pricingAlerts.isLoading || pricingAlertsAnnotations.isLoading
	const isError = pricingAlerts.isError || pricingAlertsAnnotations.isError
	if (isLoading) {
		return <AlertsListSkeleton />
	}
	if (isError) {
		return <FatalError />
	}
	const alertsTree = getPricingAlertsTree({
		pricingAlerts: pricingAlerts.data,
		annotations: pricingAlertsAnnotations.data,
	})
	const handleNavigate = (ids: string[]) => {
		close()
		navigate({
			to:
				optimizationGroupIds?.length === 1
					? `/p/og/${optimizationGroupIds[0]}/products${getFiltersQueryString([
							{
								name: 'pricing_campaign_id',
								operation: 'in',
								value: [pricingCampaign.id],
							},
							{
								name: 'alerts',
								operation: Operators.ANY_OF,
								value: ids,
							},
						])}`
					: `/p/products${getFiltersQueryString([
							{
								name: 'pricing_campaign_id',
								operation: 'in',
								value: [pricingCampaign.id],
							},
							{
								name: 'alerts',
								operation: Operators.ANY_OF,
								value: ids,
							},
						])}`,
		})
	}
	const totalAlerts = sum(pricingAlerts?.data?.by_level?.map(prop('products')))
	const totalAlertsFormatted = formatNumber(totalAlerts || 0)

	return (
		<Layout className='h-full overflow-y-hidden'>
			<SheetHeader>
				<SheetTitle className='text-base'>{pricingCampaign?.name}</SheetTitle>
				<SheetDescription>
					{t('pricing_alerts.drawer.pricing_campaign.subtitle', {
						total: totalAlertsFormatted,
						defaultValue: `${totalAlertsFormatted} alerts total for selected PC`,
					})}
				</SheetDescription>
			</SheetHeader>
			<Content className='overflow-y-auto'>
				<div className='w-full px-4'>
					{!alertsTree?.length ? (
						<Empty className='flex h-full flex-col items-center justify-center'>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<CheckCircleIcon height={50} width={50} />
								</EmptyMedia>
								<EmptyTitle>
									{t('general_everything_is_ok', {
										defaultValue: 'Everything is ok',
									})}
								</EmptyTitle>
								<EmptyDescription>
									{t('general_everything_is_ok_description', {
										defaultValue:
											'Everything is ok. You have no pricing alerts.',
									})}
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<PricingAlerts
							className='pb-20'
							collapsedByDefault
							alertsOf='pc'
							alertsTree={alertsTree}
							onNavigate={handleNavigate}
						/>
					)}
				</div>
			</Content>
		</Layout>
	)
}

export function GlobalPricingAlerts({ close }: { close(): void }) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const navigate = useNavigate()
	const pricingAlertsAnnotations = usePricingAlertsAnnotationsQuery()
	const pricingAlerts = usePricingAlertsQuery({ currency })
	const isLoading =
		pricingAlertsAnnotations.isLoading || pricingAlerts.isLoading
	const isError = pricingAlertsAnnotations.isError || pricingAlerts.isError
	if (isLoading) {
		return <AlertsListSkeleton />
	}
	if (isError) {
		return <FatalError />
	}
	const alertsTree = getPricingAlertsTree({
		pricingAlerts: pricingAlerts.data,
		annotations: pricingAlertsAnnotations.data,
	})
	const handleNavigate = (ids: string[]) => {
		close()
		navigate({
			to: `/p/products${getFiltersQueryString([
				{ name: 'alerts', operation: Operators.ANY_OF, value: ids },
			])}`,
		})
	}

	return (
		<Layout className='h-full overflow-y-hidden'>
			<SheetHeader>
				<SheetTitle className='text-base'>
					{t('pricing_alerts.drawer.assortment.title', {
						defaultValue: 'Company level alerts',
					})}
				</SheetTitle>
				<SheetDescription>
					{t('pricing_alerts.drawer.assortment.subtitle', {
						defaultValue:
							'Showing alerts for all OGs. % values are based on revenue difference.',
					})}
				</SheetDescription>
			</SheetHeader>
			<Content className='overflow-y-auto'>
				<div className='w-full px-4'>
					{!alertsTree?.length ? (
						<Empty className='flex h-full flex-col items-center justify-center'>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<CheckCircleIcon height={50} width={50} />
								</EmptyMedia>
								<EmptyTitle>
									{t('general_everything_is_ok', {
										defaultValue: 'Everything is ok',
									})}
								</EmptyTitle>
								<EmptyDescription>
									{t('general_everything_is_ok_description', {
										defaultValue:
											'Everything is ok. You have no pricing alerts.',
									})}
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<PricingAlerts
							className='pb-20'
							collapsedByDefault
							alertsOf='assortment'
							alertsTree={alertsTree}
							onNavigate={handleNavigate}
						/>
					)}
				</div>
			</Content>
		</Layout>
	)
}
const AlertsListSkeleton = () => (
	<Layout className='h-full overflow-y-hidden'>
		<Header className='border-base flex items-center justify-between border-b border-solid p-5'>
			<div>
				<Skeleton className='h-6 w-24 rounded-lg' />
				<Skeleton className='mt-2 h-4 w-72 rounded-lg' />
			</div>
		</Header>
		<div className='flex w-full flex-col justify-center gap-2 p-5'>
			<Skeleton className='h-12 w-full' />
			<Skeleton className='h-12 w-full opacity-70' />
			<Skeleton className='h-12 w-full opacity-50' />
			<Skeleton className='h-12 w-full opacity-30' />
			<Skeleton className='h-12 w-full opacity-10' />
		</div>
	</Layout>
)
